import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import sidemenucss from "../../css/sidemenu.module.css";
import commoncss from "../../css/common.module.css";

/**
 * PreviewSidebar
 */
const PreviewSidebar = ({
  menuItems,
  title,
  activeItem,
  showData,
  templateData,
  setSelCat,
  redirectNote,
  setRedirectNote,
  openSubmenu,
  setOpenSubmenu,
  activeSubmenu,
  setActiveSubmenu,
  isLoading,
  isScrolling,
}) => {
  const routerHistory = useHistory();
  const isRedirectingRef = useRef(false);

  const getNoteKeyByMenuIndex = (index) => {
    switch (index) {
      case 0: return "BS";
      case 1: return "PL";
      case 2: return "socie";
      case 3: return "cashflow";
      case 4: return "generalNotes";
      case 5: return "BSNotes";
      case 6: return "PLNotes";
      case 7: return "disclosure";
      default: return null;
    }
  };

  useEffect(() => {
    if (redirectNote && templateData) {
      isRedirectingRef.current = true;
      const targetNoteKey = redirectNote.sourceCategory === "BS" ? "BSNotes" : "PLNotes";
      const mIdx = targetNoteKey === "BSNotes" ? 5 : 6;
      
      setOpenSubmenu(mIdx);
      setSelCat(targetNoteKey);

      const arr = templateData?.[targetNoteKey] || [];
      const normalize = (str) => String(str || "").replace(/[^a-z0-9]/gi, "").toLowerCase();
      
      const searchArg = String(redirectNote.notes || "").trim().toLowerCase();
      const searchPart = String(redirectNote.l2Name || "").trim().toLowerCase();
      const normPart = normalize(searchPart);

      let topIdx = -1;

      // 1. Exact Match on Note Header Name (row name)
      if (normPart) {
        topIdx = arr.findIndex(n => normalize(n.name) === normPart);
      }

      // 2. Deep Search Exact Match (Table Title or Table Row Name)
      if (topIdx === -1 && normPart) {
        for (let i = 0; i < arr.length; i++) {
          const note = arr[i];
          const hasMatch = (note.data || []).some(block => {
            if (block.dataType === "table") {
              const tableTitleNorm = normalize(block.tableTitle);
              if (tableTitleNorm === normPart) return true;
              return (block.tableRow || []).some(row => {
                const rowNameNorm = normalize(row.name);
                return rowNameNorm === normPart;
              });
            }
            return false;
          });
          if (hasMatch) {
            topIdx = i;
            break;
          }
        }
      }

      // 3. Try direct match by ID
      if (topIdx === -1 && redirectNote._id) {
        topIdx = arr.findIndex(n => n._id === redirectNote._id);
      }

      // 4. Try match by groupingID
      if (topIdx === -1 && redirectNote.groupingID) {
        topIdx = arr.findIndex(n => n.groupingID === redirectNote.groupingID);
      }

      // 5. Try Exact Match on Note Number
      if (topIdx === -1 && searchArg) {
        topIdx = arr.findIndex(n => String(n.notes || "").trim().toLowerCase() === searchArg);
      }

      // 6. Try Partial Match on Note Header Name
      if (topIdx === -1 && normPart) {
        topIdx = arr.findIndex(n => normalize(n.name).includes(normPart));
      }

      // 7. Deep Search Partial Match
      if (topIdx === -1 && normPart) {
        for (let i = 0; i < arr.length; i++) {
          const note = arr[i];
          const hasMatch = (note.data || []).some(block => {
            if (block.dataType === "table") {
              const tableTitleNorm = normalize(block.tableTitle);
              if (tableTitleNorm.includes(normPart)) return true;
              return (block.tableRow || []).some(row => {
                const rowNameNorm = normalize(row.name);
                return rowNameNorm.includes(normPart);
              });
            }
            return false;
          });
          if (hasMatch) {
            topIdx = i;
            break;
          }
        }
      }

      // 8. Try Partial Match on Note Number
      if (topIdx === -1 && searchArg) {
        topIdx = arr.findIndex(n => String(n.notes || "").trim().toLowerCase().includes(searchArg));
      }
      
      if (topIdx !== -1) {
        setActiveSubmenu({
          menuIndex: mIdx,
          subIndex: topIdx,
          subSubIndex: null,
          clickId: Date.now()
        });
        showData(targetNoteKey, topIdx, null);
      }
      
      if (setRedirectNote) setRedirectNote(null);
    }
  }, [redirectNote, templateData]);

  // Auto-scroll to active submenu item
  useEffect(() => {
    if (activeSubmenu.subIndex !== null) {
      const timer = setTimeout(() => {
        const idSuffix = activeSubmenu.subSubIndex !== null 
          ? `${activeSubmenu.subIndex}-${activeSubmenu.subSubIndex}`
          : `${activeSubmenu.subIndex}`;
        const el = document.getElementById("side-" + idSuffix);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [activeSubmenu]);

  useEffect(() => {
    if (!templateData) return;
    if (redirectNote) return;
    if (isRedirectingRef.current) {
      isRedirectingRef.current = false;
      return;
    }

    const noteKey = getNoteKeyByMenuIndex(openSubmenu);
    if (!noteKey) return;

    if (noteKey === "BS" || noteKey === "PL") {
      const cat = noteKey;
      const list = templateData?.grouping
          ?.map((note, idx) => ({ note, idx }))
          .filter(({ note }) => note.category === cat) || [];

      if (list.length > 0) {
        const firstIdx = list[0].idx;
        setActiveSubmenu({
          menuIndex: openSubmenu,
          subIndex: firstIdx,
          subSubIndex: null,
          clickId: Date.now()
        });
        showData(noteKey, firstIdx, null);
        setSelCat(noteKey);
        return;
      }
    }

    const arr = templateData?.[noteKey] || [];
    if (arr.length > 0) {
      setActiveSubmenu({
        menuIndex: openSubmenu,
        subIndex: 0,
        subSubIndex: null,
        clickId: Date.now()
      });
      showData(noteKey, 0, null);
      setSelCat(noteKey);
      return;
    }

    setActiveSubmenu({ menuIndex: openSubmenu, subIndex: 0, subSubIndex: null, clickId: Date.now() });
    showData(noteKey, 0, null);
    setSelCat(noteKey);
  }, [openSubmenu, templateData]);

  return (
    <div className={`${sidemenucss.sidemenuContainer} ${sidemenucss.sidemenuBackgroundClr} w-100`}>
      <div className={sidemenucss.sidemenuHeader} style={{ minHeight: "15vh" }}>
        <span className={sidemenucss.sidemenuTitle} style={{ marginTop: "20px" }}>{title}</span>
        <span className={sidemenucss.wsName} style={{ marginTop: "-5px" }}>{templateData?.name || "Workspace name"}</span>
      </div>

      <div className={`${sidemenucss.sidemenuContent} ${sidemenucss.sidemenuLinksFontSize} ${sidemenucss.sidemenuContentTop} ${commoncss.hideScroll} pt-0`}>
        <ul className={sidemenucss.sidemenuList}>
          {menuItems?.map((menu, mIdx) => {
            const noteKey = getNoteKeyByMenuIndex(mIdx);
            return (
              <div key={mIdx}>
                <li
                  onClick={() => {
                    setOpenSubmenu(mIdx);
                    setSelCat(noteKey);
                  }}
                  className={`${sidemenucss.sidemenuItem} ${openSubmenu === mIdx ? sidemenucss.sidemenuItemActive : ""}`}
                >
                  <span className={sidemenucss.sidemenuItemname}>
                    <div className={sidemenucss.sideMenuItemContainer}>{menu.label}</div>
                  </span>
                </li>

                {(isLoading || isScrolling) && openSubmenu === mIdx && (
                  <div className="d-flex justify-content-center p-2">
                    <style>{`@keyframes spin {0% {transform: rotate(0deg);} 100% {transform: rotate(360deg);} }`}</style>
                    <div style={{
                      display: "grid",
                      placeItems: "center",
                      background: "transparent",
                      boxShadow: "none",
                    }}>
                      <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        border: "4px solid rgba(0, 0, 0, 0.1)",
                        borderTopColor: "#47b36b",
                        animation: "spin 0.9s linear infinite",
                      }} />
                    </div>
                  </div>
                )}

                {noteKey === "BS" && openSubmenu === mIdx && (
                  <div className={sidemenucss.submenuContainer}>
                    {templateData?.grouping
                      ?.map((note, idx) => ({ note, idx }))
                      .filter(({ note }) => note.category === "BS")
                      .map(({ note, idx }) => (
                        <div
                          key={idx}
                          id={"side-" + idx}
                          className={`${sidemenucss.submenuItemNotes} ${activeSubmenu.menuIndex === mIdx && activeSubmenu.subIndex === idx ? sidemenucss.activeSubMenu : ""}`}
                          onClick={() => {
                            setActiveSubmenu({ menuIndex: mIdx, subIndex: idx, subSubIndex: null, clickId: Date.now() });
                            showData("BS", idx, null);
                          }}
                        >
                          {note.name}
                        </div>
                      ))}
                  </div>
                )}

                {noteKey === "PL" && openSubmenu === mIdx && (
                  <div className={sidemenucss.submenuContainer}>
                    {templateData?.grouping
                      ?.map((note, idx) => ({ note, idx }))
                      .filter(({ note }) => note.category === "PL")
                      .map(({ note, idx }) => (
                        <div
                          key={idx}
                          id={"side-" + idx}
                          className={`${sidemenucss.submenuItemNotes} ${activeSubmenu.menuIndex === mIdx && activeSubmenu.subIndex === idx ? sidemenucss.activeSubMenu : ""}`}
                          onClick={() => {
                            setActiveSubmenu({ menuIndex: mIdx, subIndex: idx, subSubIndex: null, clickId: Date.now() });
                            showData("PL", idx, null);
                          }}
                        >
                          {note.name}
                        </div>
                      ))}
                  </div>
                )}

                {noteKey && noteKey !== "BS" && noteKey !== "PL" && openSubmenu === mIdx && (
                  <div className={sidemenucss.submenuContainer}>
                    {(templateData[noteKey] || []).map((topItem, topIdx) => (
                      <div key={topIdx} style={{ marginBottom: 6 }}>
                        <div
                          id={"side-" + topIdx}
                          className={`${sidemenucss.submenuItemNotes} ${activeSubmenu.menuIndex === mIdx && activeSubmenu.subIndex === topIdx && activeSubmenu.subSubIndex === null ? sidemenucss.activeSubMenu : ""}`}
                          onClick={() => {
                            setActiveSubmenu({ menuIndex: mIdx, subIndex: topIdx, subSubIndex: null, clickId: Date.now() });
                            showData(noteKey, topIdx, null);
                          }}
                        >
                          {topItem.name}
                        </div>

                        {Array.isArray(topItem.subNotes) && topItem.subNotes.length > 0 && (
                          <div style={{ paddingLeft: 12, marginTop: 4 }}>
                            {topItem.subNotes.map((sn, sIdx) => (
                              <div
                                key={sIdx}
                                id={`side-${topIdx}-${sIdx}`}
                                className={`${sidemenucss.submenuItemNotes} ${activeSubmenu.menuIndex === mIdx && activeSubmenu.subIndex === topIdx && activeSubmenu.subSubIndex === sIdx ? sidemenucss.activeSubMenu : ""}`}
                                style={{ fontSize: 13, paddingLeft: 6 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveSubmenu({ menuIndex: mIdx, subIndex: topIdx, subSubIndex: sIdx, clickId: Date.now() });
                                  showData(noteKey, topIdx, sIdx);
                                }}
                              >
                                {sn.name}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default PreviewSidebar;
